'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Divider,
  IconButton,
  Avatar,
  alpha,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  LocalShipping,
  CheckCircle,
  ArrowForward,
  Star,
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandMore,
  Psychology,
  Inventory2,
  Business,
  SupportAgent,
  AccountBalanceWallet,
  QrCode2,
  Smartphone,
  TwoWheeler,
  DoneAll,
  Bolt,
  People,
  BarChart,
  Verified,
  RocketLaunch,
  CurrencyRupee,
  AccessTime,
  Route,
  AltRoute,
  NavigateNext,
  NavigateBefore,
  OpenInNew,
  Dashboard,
  HelpOutline,
  Description as DescriptionIcon,
  ContactSupport,
  Groups,
  Repeat,
  Shield,
  Speed,
  Payments,
} from '@mui/icons-material';
import { brandingConfig } from '@/config/branding';
import { useTranslation } from '@/kit/useTranslation';
import { LanguageSwitch } from '@/kit/LanguageSwitch';
import HeroLogisticsAnimation from './HeroLogisticsAnimation';

// ─── Brand colours (light + teal — clean, positive, professional) ─────────────
const C = {
  heroBg: 'linear-gradient(135deg, #faf8f5 0%, #f4f1ed 40%, #ede9e3 100%)',
  brand: '#00BFA5',
  brandDark: '#00897B',
  brandLight: '#E0F2F1',
  brandGlow: 'rgba(0,191,165,0.25)',
  accent: '#4DD0E1',
  darkBg: '#0a0f0e',
  surface: '#f9fafb',
  white: '#ffffff',
  textDark: '#111827',
  textMid: '#374151',
  textMuted: '#6b7280',
  border: '#e5e7eb',
  amber: '#f59e0b',
};

// ─── Configurable stats — translation keys ───────────────────────────────────
const STAT_ICONS = [CurrencyRupee, AccessTime, Route, QrCode2];
const STAT_KEYS = ['stat1', 'stat2', 'stat3', 'stat4'];

// ─── Headline keys ────────────────────────────────────────────────────────────
const HEADLINE_KEYS = ['h1', 'h2', 'h3', 'h4'];

// ─── Keyframes ────────────────────────────────────────────────────────────────
const keyframes = `
  @keyframes shimmer {
    0%   { background-position: -400% center; }
    100% { background-position:  400% center; }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes cardEnter {
    from { opacity: 0; transform: translateY(24px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes blinkDot {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-10px); }
  }
  @keyframes floatSlow {
    0%, 100% { transform: translateY(0px) rotate(-3deg); }
    50%       { transform: translateY(-14px) rotate(3deg); }
  }
  @keyframes pulse {
    0%   { box-shadow: 0 0 0 0 rgba(0,191,165,0.5); }
    70%  { box-shadow: 0 0 0 16px rgba(0,191,165,0); }
    100% { box-shadow: 0 0 0 0 rgba(0,191,165,0); }
  }
  @keyframes drift {
    0%   { transform: translate(0, 0); }
    25%  { transform: translate(8px,  -12px); }
    50%  { transform: translate(-6px, 4px); }
    75%  { transform: translate(4px,  10px); }
    100% { transform: translate(0, 0); }
  }
  @keyframes moveDot {
    0%   { left: 10%; }
    100% { left: 88%; }
  }
  @keyframes marqueeScroll {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  @keyframes driveOut {
    0%   { offset-distance: 0%; opacity: 0; }
    8%   { opacity: 1; }
    48%  { offset-distance: 100%; opacity: 1; }
    50%  { offset-distance: 100%; opacity: 0; }
    51%  { offset-distance: 0%; opacity: 0; }
    59%  { opacity: 1; }
    98%  { offset-distance: 100%; opacity: 1; }
    100% { offset-distance: 100%; opacity: 0; }
  }
  @keyframes driveReturn {
    0%   { offset-distance: 100%; opacity: 0; }
    8%   { opacity: 1; }
    48%  { offset-distance: 0%; opacity: 1; }
    50%  { offset-distance: 0%; opacity: 0; }
    51%  { offset-distance: 100%; opacity: 0; }
    59%  { opacity: 1; }
    98%  { offset-distance: 0%; opacity: 1; }
    100% { offset-distance: 0%; opacity: 0; }
  }
  @keyframes hubPing {
    0%   { transform: translate(-50%,-50%) scale(1); opacity: 0.5; }
    100% { transform: translate(-50%,-50%) scale(2.8); opacity: 0; }
  }
`;

// ─── Rotating headline ────────────────────────────────────────────────────────

function HeadlineRotator({ headlines }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % headlines.length);
        setVisible(true);
      }, 400);
    }, 3400);
    return () => clearInterval(id);
  }, [headlines.length]);
  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        background: `linear-gradient(90deg, ${C.brand}, ${C.accent}, ${C.brand})`,
        backgroundSize: '300% auto',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        animation: 'shimmer 4s linear infinite',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-8px)',
        transition: 'opacity 0.35s ease, transform 0.35s ease',
        height: { xs: '8rem', sm: '6.2rem', md: '8rem' },
        overflow: 'hidden',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}
    >
      {headlines[idx]}
    </Box>
  );
}

// ─── Section badge ────────────────────────────────────────────────────────────
function SectionBadge({ label, color }) {
  const fg = color || C.brand;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: alpha(fg, 0.1),
        color: fg,
        fontWeight: 700,
        fontSize: '0.72rem',
        letterSpacing: 1.1,
        textTransform: 'uppercase',
        border: `1px solid ${alpha(fg, 0.22)}`,
        mb: 2,
      }}
    />
  );
}

// ─── Hero — live dashboard preview (right side, fully static, no jump) ────────
function HeroDashboard() {
  return (
    <Box
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        bgcolor: 'rgba(255,255,255,0.05)',
        border: `1px solid ${alpha(C.brand, 0.2)}`,
        backdropFilter: 'blur(20px)',
        boxShadow: `0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px ${alpha(C.brand, 0.1)}`,
        animation: 'cardEnter 0.8s ease both',
        animationDelay: '0.3s',
        maxWidth: 460,
        ml: 'auto',
      }}
    >
      {/* Title bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.4,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {['#ff5f57', '#ffbd2e', '#28c840'].map((c) => (
          <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
        ))}
        <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.7rem', ml: 1, flex: 1 }}>
          terrakite.com · Dashboard
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: C.brand,
              animation: 'blinkDot 1.6s ease infinite',
            }}
          />
          <Typography sx={{ color: C.brand, fontSize: '0.62rem', fontWeight: 700 }}>
            LIVE
          </Typography>
        </Stack>
      </Box>

      {/* Map area */}
      <Box
        sx={{
          position: 'relative',
          height: 176,
          background: 'linear-gradient(135deg, #0d2b28 0%, #071a17 100%)',
          overflow: 'hidden',
        }}
      >
        {/* Grid lines */}
        {[...Array(6)].map((_, i) => (
          <Box
            key={`h${i}`}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${i * 30}px`,
              height: '1px',
              bgcolor: 'rgba(0,191,165,0.07)',
            }}
          />
        ))}
        {[...Array(9)].map((_, i) => (
          <Box
            key={`v${i}`}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${i * 52}px`,
              width: '1px',
              bgcolor: 'rgba(0,191,165,0.07)',
            }}
          />
        ))}
        {/* Route */}
        <Box
          sx={{
            position: 'absolute',
            top: 54,
            left: 64,
            right: 72,
            height: 2,
            background: `linear-gradient(90deg, ${C.brand}, ${C.accent})`,
            borderRadius: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 44,
            right: 72,
            width: 2,
            height: 32,
            background: `linear-gradient(180deg, ${C.accent}, ${C.brand})`,
            borderRadius: 1,
          }}
        />
        {/* Dots */}
        <Box
          sx={{
            position: 'absolute',
            top: 46,
            left: 60,
            width: 15,
            height: 15,
            borderRadius: '50%',
            bgcolor: C.brand,
            border: '3px solid #fff',
            boxShadow: `0 0 10px ${C.brand}`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 72,
            right: 68,
            width: 15,
            height: 15,
            borderRadius: '50%',
            bgcolor: C.accent,
            border: '3px solid #fff',
            boxShadow: `0 0 10px ${C.accent}`,
          }}
        />
        {/* Rider */}
        <Box
          sx={{
            position: 'absolute',
            top: 36,
            left: '42%',
            bgcolor: '#fff',
            borderRadius: '50%',
            p: 0.45,
            boxShadow: '0 3px 10px rgba(0,0,0,0.4)',
          }}
        >
          <TwoWheeler sx={{ color: C.brand, fontSize: 15 }} />
        </Box>
        {/* Badges */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            bgcolor: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(8px)',
            border: `1px solid ${alpha(C.brand, 0.4)}`,
            borderRadius: 1.5,
            px: 1.4,
            py: 0.5,
          }}
        >
          <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>
            <Box component="span" sx={{ color: C.brand }}>
              ETA{' '}
            </Box>
            4 min
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            bgcolor: alpha(C.brand, 0.15),
            border: `1px solid ${alpha(C.brand, 0.3)}`,
            borderRadius: 1.5,
            px: 1.4,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: C.brand,
              animation: 'blinkDot 1s ease infinite',
            }}
          />
          <Typography sx={{ color: C.brand, fontSize: '0.68rem', fontWeight: 700 }}>
            On the Way
          </Typography>
        </Box>
      </Box>

      {/* Metric row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {[
          { label: 'Active', value: '3', icon: LocalShipping, color: C.brand },
          { label: 'Today', value: '12', icon: DoneAll, color: C.accent },
          { label: 'Wallet', value: '₹2,450', icon: AccountBalanceWallet, color: C.amber },
        ].map((m) => (
          <Box
            key={m.label}
            sx={{
              px: 2,
              py: 1.6,
              borderRight: '1px solid rgba(255,255,255,0.05)',
              '&:last-child': { borderRight: 'none' },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.6} mb={0.3}>
              <m.icon sx={{ color: m.color, fontSize: 12 }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.62rem' }}>
                {m.label}
              </Typography>
            </Stack>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.92rem' }}>
              {m.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Recent deliveries */}
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.35)',
            fontSize: '0.62rem',
            letterSpacing: 1,
            textTransform: 'uppercase',
            mb: 1.2,
          }}
        >
          Recent Deliveries
        </Typography>
        {[
          {
            id: 'TK-4821',
            route: 'Banjara Hills → Gachibowli',
            status: 'Delivered',
            color: C.brand,
          },
          {
            id: 'TK-4820',
            route: 'Jubilee Hills → Hitech City',
            status: 'In Transit',
            color: C.accent,
          },
          { id: 'TK-4819', route: 'Kondapur → Madhapur', status: 'Picked Up', color: C.amber },
        ].map((d) => (
          <Stack
            key={d.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              py: 0.75,
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }}
              />
              <Box>
                <Typography
                  sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem', fontWeight: 600 }}
                >
                  {d.id}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: '0.62rem' }}>
                  {d.route}
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={d.status}
              size="small"
              sx={{
                bgcolor: alpha(d.color, 0.13),
                color: d.color,
                fontSize: '0.6rem',
                fontWeight: 700,
                height: 19,
                border: `1px solid ${alpha(d.color, 0.28)}`,
              }}
            />
          </Stack>
        ))}
      </Box>
    </Box>
  );
}

// ─── How-it-works step ────────────────────────────────────────────────────────
function HowStep({ number, icon: Icon, title, desc, isLast }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        alignItems: { xs: 'flex-start', md: 'center' },
        flex: 1,
        position: 'relative',
        gap: { xs: 2.5, md: 0 },
      }}
    >
      {/* Circle Icon Container */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: { xs: 44, md: 68 },
            height: { xs: 44, md: 68 },
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${C.brand}, ${C.accent})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: { xs: 0, md: 2 },
            boxShadow: `0 8px 24px ${alpha(C.brand, 0.3)}`,
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Typography
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              bgcolor: C.brandDark,
              color: '#fff',
              borderRadius: '50%',
              width: { xs: 18, md: 22 },
              height: { xs: 18, md: 22 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: { xs: '0.6rem', md: '0.7rem' },
              fontWeight: 800,
            }}
          >
            {number}
          </Typography>
          <Icon sx={{ color: '#fff', fontSize: { xs: 18, md: 28 } }} />
        </Box>

        {/* Vertical line for mobile */}
        {!isLast && (
          <Box
            sx={{
              position: 'absolute',
              top: 44,
              left: '50%',
              width: 2,
              height: 40,
              background: `linear-gradient(180deg, ${alpha(C.brand, 0.45)}, transparent)`,
              display: { xs: 'block', md: 'none' },
              zIndex: 1,
            }}
          />
        )}
      </Box>

      {/* Connection line for desktop */}
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            top: 34,
            left: '60%',
            width: '40%',
            height: 2,
            background: `linear-gradient(90deg, ${alpha(C.brand, 0.45)}, transparent)`,
            display: { xs: 'none', md: 'block' },
            zIndex: 1,
          }}
        />
      )}

      <Box sx={{ textAlign: { xs: 'left', md: 'center' }, pt: { xs: 0.5, md: 0 } }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: C.textDark, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.85rem',
            color: C.textMid,
            lineHeight: 1.5,
            maxWidth: { xs: '100%', md: 175 },
          }}
        >
          {desc}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Tier card — compact version ─────────────────────────────────────────────
function TierCard({ tier, featured }) {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        border: featured ? `2px solid ${C.brand}` : `1px solid ${C.border}`,
        bgcolor: '#fff',
        position: 'relative',
        overflow: 'visible',
        transition: 'transform 0.25s, box-shadow 0.25s',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: featured
            ? `0 24px 56px ${alpha(C.brand, 0.18)}`
            : '0 16px 40px rgba(0,0,0,0.08)',
        },
        boxShadow: featured ? `0 8px 32px ${alpha(C.brand, 0.12)}` : '0 2px 8px rgba(0,0,0,0.04)',
      }}
    >
      {/* Popular badge */}
      {/* {featured && (
        <Box
          sx={{
            position: 'absolute',
            top: -14,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        >
          <Box sx={{ bgcolor: C.brand, borderRadius: 99, px: 2, py: 0.5 }}>
            <Typography
              sx={{ color: '#000', fontWeight: 800, fontSize: '0.7rem', letterSpacing: 0.5 }}
            >
              ⭐ Most Popular
            </Typography>
          </Box>
        </Box>
      )} */}

      {/* Card header */}
      <Box sx={{ p: { xs: 2.5, md: 3 }, pb: 2, borderBottom: `1px solid ${C.border}` }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
          <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: alpha(tier.color, 0.1) }}>
            <tier.icon sx={{ color: tier.color, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: C.textDark }}>
              {tier.name}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: C.textMuted }}>
              {tier.subtitle}
            </Typography>
          </Box>
        </Stack>
        <Typography sx={{ fontSize: '0.85rem', color: C.textMid, lineHeight: 1.7 }}>
          {tier.description}
        </Typography>
      </Box>

      {/* Features list */}
      <Box sx={{ p: { xs: 2.5, md: 3 }, flex: 1 }}>
        <Stack spacing={1.2}>
          {tier.features.map((f, i) => (
            <Stack key={i} direction="row" alignItems="flex-start" spacing={1.2}>
              <CheckCircle sx={{ color: tier.color, fontSize: 16, mt: 0.15, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.84rem', color: C.textMid, lineHeight: 1.5 }}>
                {f}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Pricing footer */}
      <Box
        sx={{
          mx: { xs: 2, md: 3 },
          mb: { xs: 2.5, md: 3 },
          mt: 0,
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          bgcolor: featured ? alpha(C.brand, 0.06) : C.surface,
          border: `1px solid ${featured ? alpha(C.brand, 0.2) : C.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.72rem',
            color: C.textMuted,
            mb: 0.5,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontWeight: 600,
          }}
        >
          Starting from
        </Typography>
        <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: tier.color, lineHeight: 1 }}>
          {tier.price}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Feature card ─────────────────────────────────────────────────────────────
function FeatureCard({ icon: Icon, title, desc, color }) {
  return (
    <Box
      sx={{
        p: { xs: 1.8, md: 2.5 },
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        bgcolor: '#fff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
          borderColor: alpha(color, 0.4),
        },
      }}
    >
      <Box
        sx={{
          p: 0.9,
          borderRadius: 2,
          bgcolor: alpha(color, 0.1),
          width: 'fit-content',
          mb: { xs: 1.2, md: 1.5 },
        }}
      >
        <Icon sx={{ color, fontSize: { xs: 18, md: 21 } }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: '0.82rem', md: '0.92rem' },
          color: C.textDark,
          mb: 0.5,
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ fontSize: { xs: '0.72rem', md: '0.81rem' }, color: C.textMuted, lineHeight: 1.5 }}
      >
        {desc}
      </Typography>
    </Box>
  );
}

// ─── Testimonials carousel ────────────────────────────────────────────────────
const TESTIMONIAL_KEYS = ['t1', 't2', 't3', 't4'];

function TestimonialCard({ r }) {
  const locationTag = r.role.includes(', ') ? r.role.split(', ').slice(1).join(', ') : '';
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${C.border}`,
        bgcolor: '#fff',
        p: { xs: 2.5, md: 3 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 0.25s, transform 0.25s',
        '&:hover': { boxShadow: '0 16px 48px rgba(0,0,0,0.1)', transform: 'translateY(-3px)' },
      }}
    >
      {/* Decorative quote mark */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 18,
          fontSize: '5.5rem',
          color: alpha(C.brand, 0.07),
          lineHeight: 1,
          fontFamily: 'Georgia, serif',
          fontWeight: 900,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        &ldquo;
      </Box>
      {/* Teal accent left bar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 4,
          height: '100%',
          background: `linear-gradient(180deg, ${C.brand}, ${C.accent})`,
          borderRadius: '4px 0 0 4px',
        }}
      />
      <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Stack direction="row" spacing={0.3} mb={1.5}>
          {[...Array(5)].map((_, j) => (
            <Star key={j} sx={{ color: C.amber, fontSize: 13 }} />
          ))}
        </Stack>
        {locationTag ? (
          <Chip
            label={locationTag}
            size="small"
            sx={{
              bgcolor: alpha(C.brand, 0.08),
              color: C.brandDark,
              fontWeight: 700,
              fontSize: '0.67rem',
              height: 22,
              mb: 1.5,
              alignSelf: 'flex-start',
            }}
          />
        ) : null}
        <Typography
          sx={{
            fontSize: '0.88rem',
            color: C.textMid,
            lineHeight: 1.78,
            mb: 2.5,
            fontStyle: 'italic',
            flex: 1,
          }}
        >
          &ldquo;{r.text}&rdquo;
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              background: `linear-gradient(135deg, ${C.brand}, ${C.accent})`,
              color: '#000',
              fontWeight: 800,
              width: 38,
              height: 38,
              fontSize: '0.9rem',
            }}
          >
            {r.name[0]}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '0.87rem', color: C.textDark }}>
              {r.name}
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', color: C.textMuted }}>{r.role}</Typography>
          </Box>
        </Stack>
      </Box>
    </Card>
  );
}

function TestimonialsCarousel({ testimonials }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const perPage = isMobile ? 1 : 2;
  const totalPages = Math.ceil(testimonials.length / perPage);
  const [page, setPage] = useState(0);
  const [visible, setVisible] = useState(true);

  const goTo = useCallback((p) => {
    setVisible(false);
    setTimeout(() => {
      setPage(p);
      setVisible(true);
    }, 260);
  }, []);

  const prev = () => page > 0 && goTo(page - 1);
  const next = () => page < totalPages - 1 && goTo(page + 1);
  const shown = testimonials.slice(page * perPage, page * perPage + perPage);

  return (
    <Box>
      <Box
        sx={{
          minHeight: { xs: 300, md: 280 },
          transition: 'opacity 0.26s ease',
          opacity: visible ? 1 : 0,
        }}
      >
        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
          {shown.map((r, i) => (
            <Grid item xs={12} md={6} key={`${page}-${i}`} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%' }}>
                <TestimonialCard r={r} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} mt={4}>
        <IconButton
          onClick={prev}
          disabled={page === 0}
          size="small"
          sx={{
            border: `1px solid ${C.border}`,
            bgcolor: '#fff',
            '&:not(:disabled):hover': { bgcolor: C.brandLight, borderColor: C.brand },
          }}
        >
          <NavigateBefore fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={0.8}>
          {[...Array(totalPages)].map((_, i) => (
            <Box
              key={i}
              onClick={() => goTo(i)}
              sx={{
                width: i === page ? 22 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor: i === page ? C.brand : C.border,
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}
            />
          ))}
        </Stack>
        <IconButton
          onClick={next}
          disabled={page === totalPages - 1}
          size="small"
          sx={{
            border: `1px solid ${C.border}`,
            bgcolor: '#fff',
            '&:not(:disabled):hover': { bgcolor: C.brandLight, borderColor: C.brand },
          }}
        >
          <NavigateNext fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

// ─── Data (icon/color only — text resolved via t()) ──────────────────────────
const TIER_META = [
  { key: 'regular', icon: LocalShipping, color: C.brand, featured: false },
  { key: 'business', icon: Business, color: C.brandDark, featured: true },
  { key: 'bulk', icon: Inventory2, color: C.accent, featured: false },
];

const FEATURE_META = [
  { key: 'noHub', icon: Bolt, color: C.amber },
  { key: 'mobile', icon: Smartphone, color: C.brand },
  { key: 'wallet', icon: AccountBalanceWallet, color: C.brandDark },
  { key: 'qr', icon: QrCode2, color: C.accent },
  { key: 'gps', icon: Route, color: '#f97316' },
  { key: 'support', icon: SupportAgent, color: '#ec4899' },
  { key: 'multiStop', icon: AltRoute, color: '#6366f1' },
];

const FAQ_KEYS = ['1', '2', '3', '4', '5'];

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function LandingPageV2() {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showDesktopNav = useMediaQuery(theme.breakpoints.up('lg'));
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const isAuthenticated = useSelector((state) => state.auth?.isLoggedIn);

  // Build translated data arrays
  const headlines = useMemo(() => HEADLINE_KEYS.map((k) => t(`landing.headlines.${k}`)), [t]);

  const stats = useMemo(
    () =>
      STAT_KEYS.map((k, i) => ({
        key: k,
        value: t(`landing.stats.${k}Value`),
        label: t(`landing.stats.${k}Label`),
        icon: STAT_ICONS[i],
      }))
        // Filter out missing/placeholder translations and the second stat if desired
        .filter((s) => s.value && s.value.toLowerCase() !== 'stat2value' && s.key !== 'stat2'),
    [t]
  );

  const shippingTiers = useMemo(
    () =>
      TIER_META.map((m) => {
        const features = [];
        for (let n = 1; n <= 12; n += 1) {
          const feature = t(`landing.tiers.${m.key}.f${n}`);
          if (!feature || feature.toLowerCase() === `f${n}`) break;
          features.push(feature);
        }

        return {
          ...m,
          name: t(`landing.tiers.${m.key}.name`),
          subtitle: t(`landing.tiers.${m.key}.subtitle`),
          price: t(`landing.tiers.${m.key}.price`),
          description: t(`landing.tiers.${m.key}.description`),
          features,
        };
      })
        // drop any rows where translation fell back to generic placeholder
        .filter((tier) => tier.name && tier.name.toLowerCase() !== 'name'),
    [t]
  );

  const features = useMemo(
    () =>
      FEATURE_META.map((m) => ({
        icon: m.icon,
        color: m.color,
        title: t(`landing.featureCards.${m.key}Title`),
        desc: t(`landing.featureCards.${m.key}Desc`),
      })),
    [t]
  );

  const faqs = useMemo(
    () =>
      FAQ_KEYS.map((k) => ({
        q: t(`landing.faqItems.q${k}`),
        a: t(`landing.faqItems.a${k}`),
      })),
    [t]
  );

  const testimonials = useMemo(
    () =>
      TESTIMONIAL_KEYS.map((k) => ({
        name: t(`landing.testimonialItems.${k}Name`),
        role: t(`landing.testimonialItems.${k}Role`),
        text: t(`landing.testimonialItems.${k}Text`),
      })),
    [t]
  );

  useEffect(() => {
    const cb = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', cb);
    return () => window.removeEventListener('scroll', cb);
  }, []);

  const handleGetStarted = () => router.push(isAuthenticated ? '/home' : '/login');

  const handleGetPricing = async () => {
    setQuoteLoading(true);
    router.push('/estimate');
    setTimeout(() => setQuoteLoading(false), 4000);
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { label: t('landing.nav.features'), id: 'features', icon: Bolt },
    { label: t('landing.nav.pricing'), id: 'pricing', icon: CurrencyRupee },
    { label: 'Get an Estimate', href: '/estimate', icon: CurrencyRupee },
    { label: 'Pilot Portal', href: '/pilot', icon: RocketLaunch },
    { label: t('landing.sectionBadges.howItWorks'), id: 'how', icon: Route },
    { label: t('landing.sectionBadges.forPartners'), id: 'partners', icon: Groups },
    { label: t('landing.nav.support'), href: '/support', icon: ContactSupport },
    { label: t('landing.nav.about'), href: '/terms', icon: DescriptionIcon },
  ];

  return (
    <>
      <style>{keyframes}</style>

      {/* Modern Drawer Nav */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        elevation={0}
        PaperProps={{
          sx: {
            width: '85%',
            maxWidth: 360,
            bgcolor: '#fff',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Image
            src="/assets/terrakite-teal-logo.png"
            alt="Logo"
            width={140}
            height={32}
            style={{ objectFit: 'contain' }}
            priority
          />
          <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ bgcolor: '#f5f5f5' }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <List sx={{ p: 2 }}>
          {navLinks.map((l) => (
            <ListItem key={l.label} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  setMobileMenuOpen(false);
                  l.href ? router.push(l.href) : scrollTo(l.id);
                }}
                sx={{
                  borderRadius: 3,
                  py: 1.8,
                  px: 2,
                  '&:hover': {
                    bgcolor: alpha(C.brand, 0.08),
                    '& .MuiListItemIcon-root': { color: C.brand },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 46, color: '#4b5563' }}>
                  <l.icon sx={{ fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary={l.label}
                  primaryTypographyProps={{
                    fontWeight: 400,
                    fontSize: '1.05rem',
                    color: '#111827',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: 'auto', p: 3, bgcolor: '#f9fafb' }}>
          <Stack spacing={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setMobileMenuOpen(false);
                router.push('/login');
              }}
              sx={{
                borderColor: '#e5e7eb',
                color: '#111827',
                fontWeight: 700,
                textTransform: 'none',
                py: 1.5,
                borderRadius: 3,
              }}
            >
              {t('landing.nav.login')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setMobileMenuOpen(false);
                handleGetStarted();
              }}
              sx={{
                bgcolor: C.brand,
                color: '#000',
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: 3,
                py: 1.8,
                '&:hover': { bgcolor: C.brandDark, color: '#fff' },
                boxShadow: `0 8px 20px ${alpha(C.brand, 0.3)}`,
              }}
            >
              {isAuthenticated ? t('nav.dashboard') : t('landing.hero.getInstantQuote')}
            </Button>
            <Box sx={{ pt: 0.5 }}>
              <LanguageSwitch variant="sidebar" />
            </Box>
          </Stack>
        </Box>
      </Drawer>

      {/* ── Nav ──────────────────────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: { xs: 10, md: scrolled ? 12 : 16 },
          left: '50%',
          transform: 'translateX(-50%)',
          width: scrolled ? { xs: '130px', md: '170px' } : { xs: '94%', md: 'min(94%, 1200px)' },
          zIndex: 1200,
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          // Darker background at top for immediate contrast, lighter on scroll
          bgcolor: scrolled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid',
          borderColor: scrolled ? 'rgba(0,191,165,0.2)' : 'rgba(255,255,255,0.4)',
          borderRadius: scrolled ? '30px' : '18px',
          py: scrolled ? 0.6 : 0.85, // Reduced height
          px: scrolled ? 1.2 : { xs: 2, md: 2.5 },
          boxShadow: scrolled
            ? `0 16px 36px -12px ${alpha(C.brand, 0.22)}, 0 0 0 1px ${alpha(C.brand, 0.05)}`
            : '0 8px 32px -4px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'transform 0.3s',
            }}
            onClick={() => scrollTo('hero')}
          >
            <Image
              src={scrolled ? '/assets/favicon.svg' : '/assets/terrakite-teal-logo.png'}
              alt="TerraKite"
              width={scrolled ? 32 : 130}
              height={32}
              style={{
                objectFit: 'contain',
                height: scrolled ? '26px' : '26px', // Reduced height
                width: 'auto',
                transition: 'all 0.5s',
              }}
              priority
            />
          </Box>

          {showDesktopNav && !scrolled && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.1,
                overflowX: 'auto',
                flexWrap: 'nowrap',
                py: 0.5,
                mx: -1,
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              <Stack direction="row" spacing={0} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                {navLinks.map((l) => (
                  <Button
                    key={l.label}
                    onClick={() => (l.href ? router.push(l.href) : scrollTo(l.id))}
                    sx={{
                      color: C.textMid,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      px: 1.1,
                      py: 0.75,
                      borderRadius: '12px',
                      whiteSpace: 'nowrap',
                      minWidth: 'fit-content',
                      textTransform: 'none',
                      transition: 'all 0.3s',
                      '&:hover': {
                        color: C.brand,
                        bgcolor: alpha(C.brand, 0.06),
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {l.label}
                  </Button>
                ))}
              </Stack>

              <Box sx={{ width: '1px', height: '16px', bgcolor: 'rgba(0,0,0,0.08)', mx: 1 }} />

              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                <LanguageSwitch compact />
                <Button
                  onClick={() => router.push('/login')}
                  sx={{
                    color: C.textDark,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    '&:hover': { color: C.brand },
                  }}
                >
                  {t('landing.nav.login')}
                </Button>
                <Button
                  onClick={isAuthenticated ? handleGetStarted : handleGetPricing}
                  variant="contained"
                  sx={{
                    bgcolor: C.brand,
                    color: '#000',
                    borderRadius: '10px',
                    px: 2,
                    py: 0.8,
                    fontWeight: 800,
                    fontSize: '0.8rem',
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    transition: 'all 0.4s',
                    boxShadow: `0 8px 16px ${alpha(C.brand, 0.2)}`,
                    '&:hover': {
                      bgcolor: C.brandDark,
                      color: '#fff',
                      boxShadow: `0 12px 24px ${alpha(C.brand, 0.3)}`,
                      transform: 'scale(1.02)',
                    },
                  }}
                >
                  {isAuthenticated ? 'Dashboard' : t('landing.hero.getInstantQuote')}
                </Button>
              </Stack>
            </Box>
          )}

          {(!showDesktopNav || scrolled) && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{
                  color: C.textDark,
                  bgcolor: scrolled ? 'transparent' : 'rgba(255,255,255,0.5)',
                  borderRadius: scrolled ? '50%' : '12px',
                  width: 38,
                  height: 38,
                  border: scrolled ? 'none' : '1px solid rgba(0,0,0,0.05)',
                  '&:hover': { bgcolor: alpha(C.brand, 0.1) },
                }}
              >
                <MenuIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Box
        id="hero"
        sx={{
          background: 'linear-gradient(160deg, #ffffff 0%, #f7fffe 45%, #edfaf8 100%)',
          minHeight: { xs: 'auto', md: '100vh' },
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 9, md: 18 },
          pb: { xs: 0, md: 10 },
        }}
      >
        {/* ── Animation backdrop (position absolute, fills hero) ── */}
        <HeroLogisticsAnimation />

        {/* ── Left-side readability gradient ── */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            background: {
              // Mobile: no overlay needed — animation is hidden on mobile
              xs: 'none',
              md: 'linear-gradient(to right, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 32%, rgba(255,255,255,0.2) 58%, transparent 80%)',
            },
          }}
        />

        {/* Glow blobs — teal on dark */}
        <Box
          sx={{
            position: 'absolute',
            top: -120,
            right: -100,
            width: 520,
            height: 520,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(0,191,165,0.07), transparent 65%)`,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
        {/* Floating motion particles */}
        {[
          { size: 4, top: '18%', left: '6%', dur: '7s', delay: '0s' },
          { size: 6, top: '32%', left: '91%', dur: '9s', delay: '1.2s' },
          { size: 3, top: '62%', left: '82%', dur: '5.5s', delay: '2.4s' },
          { size: 5, top: '78%', left: '12%', dur: '8s', delay: '0.7s' },
          { size: 4, top: '48%', left: '4%', dur: '6s', delay: '3.1s' },
          { size: 7, top: '22%', left: '54%', dur: '10s', delay: '1.8s' },
          { size: 3, top: '55%', left: '72%', dur: '7.5s', delay: '4s' },
        ].map((p, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: p.size,
              height: p.size,
              borderRadius: '50%',
              bgcolor: C.brand,
              top: p.top,
              left: p.left,
              opacity: 0.12,
              animation: `drift ${p.dur} ease-in-out infinite`,
              animationDelay: p.delay,
              pointerEvents: 'none',
            }}
          />
        ))}
        <Box
          sx={{
            position: 'absolute',
            bottom: -60,
            left: -80,
            width: 380,
            height: 380,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(0,191,165,0.05), transparent 65%)`,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid
            container
            spacing={{ xs: 4, md: 5 }}
            alignItems="center"
            sx={{ flexWrap: { md: 'nowrap' } }}
          >
            {/* Copy */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                textAlign: { xs: 'left', md: 'left' },
                minWidth: 0,
                flexShrink: 1,
                overflow: 'hidden',
                pb: { xs: 1, md: 0 },
                pt: { xs: 2, md: 0 },
              }}
            >
              {/* ── Mobile eyebrow ── */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.8}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  mb: 1.5,
                  animation: 'fadeUp 0.5s ease both',
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: C.brand,
                    animation: 'blinkDot 2s ease infinite',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    color: C.brand,
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  Live · Direct · Instant
                </Typography>
              </Stack>

              {/* ── Desktop pill badge ── */}
              <Box
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  alignItems: 'center',
                  gap: 1.2,
                  bgcolor: alpha(C.brand, 0.08),
                  border: `1px solid ${alpha(C.brand, 0.2)}`,
                  borderRadius: 99,
                  px: 2.2,
                  py: 0.8,
                  mb: 4,
                  animation: 'fadeUp 0.6s ease both',
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: C.brand,
                    animation: 'blinkDot 2s ease infinite',
                  }}
                />
                <Typography
                  sx={{
                    color: C.brand,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}
                >
                  Next-Gen Logistics Network
                </Typography>
              </Box>

              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '3rem', sm: '2.8rem', md: '3rem' },
                  fontWeight: 900,
                  color: '#0F1A19',
                  lineHeight: 1.05,
                  mb: { xs: 0.5, md: 1 },
                  letterSpacing: { xs: -1.5, md: -1.2 },
                  animation: 'fadeUp 0.65s ease both',
                  animationDelay: '0.1s',
                }}
              >
                Ship Smarter.
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '1.45rem', sm: '2.6rem', md: '3rem' },
                  fontWeight: 900,
                  mb: { xs: 2.5, md: 3 },
                  mt: { xs: 0.5, md: 3 },
                  letterSpacing: { xs: -0.3, md: -1.2 },
                  lineHeight: { xs: 1.28, md: 1.1 },
                  animation: 'fadeUp 0.65s ease both',
                  animationDelay: '0.15s',
                  background: `linear-gradient(90deg, ${C.brand}, #00796B)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Your Cargo Moves the Moment You Book.
              </Typography>

              {/* ── Mobile stat row ── */}
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center',
                  mb: 2.5,
                  animation: 'fadeUp 0.65s ease both',
                  animationDelay: '0.2s',
                }}
              >
                {[
                  { value: '60s', label: 'to book' },
                  { value: '40%', label: 'cheaper' },
                  { value: '0', label: 'middlemen' },
                ].map((s, i) => (
                  <React.Fragment key={s.value}>
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography
                        sx={{ color: C.brand, fontWeight: 800, fontSize: '1.45rem', lineHeight: 1 }}
                      >
                        {s.value}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#6B7280',
                          fontSize: '0.62rem',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          mt: 0.25,
                        }}
                      >
                        {s.label}
                      </Typography>
                    </Box>
                    {i < 2 && (
                      <Box
                        sx={{ width: '1px', height: 30, bgcolor: 'rgba(0,0,0,0.1)', flexShrink: 0 }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </Box>

              {/* ── Desktop subtitle ── */}
              <Typography
                sx={{
                  display: { xs: 'none', md: 'block' },
                  color: '#4B5563',
                  fontSize: '1.12rem',
                  mt: 1.5,
                  mb: 4,
                  maxWidth: 500,
                  lineHeight: 1.6,
                  animation: 'fadeUp 0.65s ease both',
                  animationDelay: '0.25s',
                }}
              >
                {t('landing.heroSubtitle')}
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.5, sm: 2.5 }}
                mb={{ xs: 2, md: 4 }}
                sx={{ animation: 'fadeUp 0.65s ease both', animationDelay: '0.35s' }}
              >
                <Button
                  onClick={handleGetStarted}
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  fullWidth={isMobile}
                  sx={{
                    bgcolor: C.brand,
                    color: '#000',
                    borderRadius: 3,
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.8, md: 1.6 },
                    fontWeight: 800,
                    fontSize: '1rem',
                    textTransform: 'none',
                    boxShadow: `0 8px 32px ${alpha(C.brand, 0.42)}`,
                    animation: 'pulse 3s ease-in-out infinite',
                    animationDelay: '2s',
                    '&:hover': { bgcolor: C.brandDark, color: '#fff' },
                  }}
                >
                  {isAuthenticated ? 'Go to Dashboard' : t('landing.builtFor.startShipping')}
                </Button>
                <Button
                  onClick={handleGetPricing}
                  disabled={quoteLoading}
                  variant="outlined"
                  size="large"
                  fullWidth={isMobile}
                  startIcon={
                    quoteLoading ? <CircularProgress size={16} sx={{ color: C.brandDark }} /> : null
                  }
                  sx={{
                    borderColor: alpha(C.brandDark, 0.5),
                    color: C.brandDark,
                    borderRadius: 3,
                    px: { xs: 3, md: 3.5 },
                    py: { xs: 1.8, md: 1.6 },
                    fontWeight: 700,
                    fontSize: '1rem',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: C.brand,
                      color: C.brand,
                      bgcolor: alpha(C.brand, 0.08),
                    },
                  }}
                >
                  {quoteLoading ? 'Loading…' : t('landing.hero.getInstantQuote')}
                </Button>
              </Stack>

              {/* ── Desktop trust badges ── */}
              <Stack
                direction="row"
                spacing={3}
                flexWrap="wrap"
                useFlexGap
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  animation: 'fadeUp 0.65s ease both',
                  animationDelay: '0.45s',
                }}
              >
                {[
                  t('landing.trustBadges.noMinimums'),
                  t('landing.trustBadges.fastOnboarding'),
                  t('landing.trustBadges.instantPayouts'),
                ].map((badge) => (
                  <Stack key={badge} direction="row" alignItems="center" spacing={0.7}>
                    <CheckCircle sx={{ color: C.brandDark, fontSize: 13 }} />
                    <Typography sx={{ color: C.textDark, fontSize: '0.82rem', fontWeight: 700 }}>
                      {badge}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>

            {/* ── Desktop truck photo — right column ── */}
            <Grid
              item
              xs={false}
              md={5}
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'flex-end',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 3,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 500,
                  borderRadius: 5,
                  overflow: 'hidden',
                  boxShadow: `0 32px 80px rgba(0,0,0,0.18), 0 0 0 1.5px ${alpha(C.brand, 0.22)}`,
                  animation: 'cardEnter 0.9s ease both',
                  animationDelay: '0.3s',
                  aspectRatio: '4/3',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(to top, rgba(0,30,25,0.35) 0%, transparent 55%)`,
                    zIndex: 1,
                    pointerEvents: 'none',
                  },
                }}
              >
                <Image
                  src="/assets/images/truck4.png"
                  alt="TerraKite logistics fleet in action"
                  fill
                  style={{ objectFit: 'cover', objectPosition: 'center 48%' }}
                  sizes="(min-width:900px) 40vw, 0px"
                  priority
                />
                {/* Bottom caption bar */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 2,
                    px: 2.5,
                    py: 1.8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'linear-gradient(to top, rgba(5,20,18,0.82) 0%, transparent 100%)',
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ color: '#fff', fontWeight: 800, fontSize: '0.88rem', lineHeight: 1.2 }}
                    >
                      Partner Vehicles
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem' }}>
                      Tata Ace · Mini-trucks · Flatbeds
                    </Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={0.7}>
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: C.brand,
                        animation: 'blinkDot 1.6s ease infinite',
                      }}
                    />
                    <Typography sx={{ color: C.brand, fontSize: '0.7rem', fontWeight: 800 }}>
                      LIVE FLEET
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* ── Mobile truck photo ── */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'relative',
            width: '100%',
            height: { xs: '52vw', sm: '38vw' },
            overflow: 'hidden',
            zIndex: 3,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '38%',
              background: 'linear-gradient(to bottom, #f7fffe 10%, transparent 100%)',
              zIndex: 2,
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '28%',
              background: 'linear-gradient(to top, #e8f8f5 0%, transparent 100%)',
              zIndex: 2,
              pointerEvents: 'none',
            },
          }}
        >
          <Image
            src="/assets/images/truck4.png"
            alt="TerraKite logistics fleet"
            fill
            style={{ objectFit: 'cover', objectPosition: 'center 52%' }}
            sizes="100vw"
          />
        </Box>
        {/* ── Trust badges below photo ── */}
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={2.5}
          sx={{
            display: { xs: 'flex', md: 'none' },
            py: 1.25,
            px: 2,
            borderTop: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          {[
            t('landing.trustBadges.noMinimums'),
            t('landing.trustBadges.fastOnboarding'),
            t('landing.trustBadges.instantPayouts'),
          ].map((badge) => (
            <Stack key={badge} direction="row" alignItems="center" spacing={0.5}>
              <CheckCircle sx={{ color: C.brand, fontSize: 12 }} />
              <Typography sx={{ color: '#374151', fontSize: '0.68rem', fontWeight: 500 }}>
                {badge}
              </Typography>
            </Stack>
          ))}
        </Stack>

        {/* Wave */}
        <Box sx={{ position: 'absolute', bottom: -2, left: 0, right: 0, lineHeight: 0 }}>
          <svg
            viewBox="0 0 1440 55"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: '100%', display: 'block' }}
          >
            <path
              d="M0 55L60 46C120 37 240 19 360 14C480 9 600 19 720 26C840 33 960 40 1080 38C1200 35 1320 25 1380 20L1440 15V55H0Z"
              fill="#e8f8f5"
            />
          </svg>
        </Box>
      </Box>

      {/* ── Cluster Advantage Banner ─────────────────────────────────────── */}
      <Box
        sx={{
          py: { xs: 4, md: 5 },
          background: 'linear-gradient(135deg, #e4f7f3 0%, #f0faf9 50%, #e4f7f3 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at 15% 50%, rgba(0,191,165,0.1) 0%, transparent 55%), radial-gradient(ellipse at 85% 50%, rgba(77,208,225,0.07) 0%, transparent 55%)',
            pointerEvents: 'none',
          },
        }}
      >
        {/* Animated shimmer line */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${C.brand}, #4DD0E1, ${C.brand}, transparent)`,
            backgroundSize: '200% auto',
            animation: 'shimmer 3s linear infinite',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 2, md: 5 }} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'rgba(0,191,165,0.12)',
                    border: '1px solid rgba(0,191,165,0.3)',
                    borderRadius: 99,
                    px: 2,
                    py: 0.6,
                    mb: 2.5,
                  }}
                >
                  <Box sx={{ fontSize: '0.85rem' }}>🧠</Box>
                  <Typography
                    sx={{
                      color: C.brand,
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      letterSpacing: 0.5,
                    }}
                  >
                    SMART ROUTE CLUSTERING
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '1.65rem', md: '2.2rem' },
                    fontWeight: 900,
                    color: '#0F1A19',
                    lineHeight: 1.15,
                    mb: 2,
                    letterSpacing: '-0.5px',
                  }}
                >
                  Send 5 parcels,{' '}
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(90deg, ${C.brand}, #4DD0E1)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    pay for 1 route
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    color: '#4B5563',
                    fontSize: { xs: '0.92rem', md: '1rem' },
                    maxWidth: 440,
                    mx: { xs: 'auto', md: 0 },
                    lineHeight: 1.7,
                  }}
                >
                  Our AI groups nearby deliveries into a single optimized route — one rider, shared
                  cost. Bulk senders save up to{' '}
                  <Box component="span" sx={{ color: C.brand, fontWeight: 700 }}>
                    40% vs individual bookings
                  </Box>
                  .
                </Typography>
                <Button
                  onClick={handleGetPricing}
                  variant="contained"
                  size="medium"
                  endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                  sx={{
                    mt: 3,
                    bgcolor: C.brand,
                    color: '#000',
                    fontWeight: 800,
                    textTransform: 'none',
                    borderRadius: 3,
                    px: 3.5,
                    py: 1.3,
                    boxShadow: `0 6px 24px ${alpha(C.brand, 0.38)}`,
                    '&:hover': { bgcolor: C.brandDark, color: '#fff' },
                  }}
                >
                  Try Bulk Booking
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                direction={{ xs: 'row', sm: 'row' }}
                spacing={{ xs: 1.5, md: 2 }}
                justifyContent={{ xs: 'center', md: 'flex-end' }}
                flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
              >
                {[
                  { icon: '📦', value: '5×', label: 'Parcels, one trip' },
                  { icon: '💸', value: '40%', label: 'Cost savings' },
                  { icon: '🗺️', value: 'AI', label: 'Route optimization' },
                ].map((card, i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: { xs: '0 0 calc(33% - 8px)', sm: 1 },
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.7)',
                      border: `1px solid ${alpha(C.brand, 0.15)}`,
                      textAlign: 'center',
                      backdropFilter: 'blur(8px)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.95)',
                        borderColor: `rgba(0,191,165,0.4)`,
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 24px ${alpha(C.brand, 0.12)}`,
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: { xs: '1.4rem', md: '1.7rem' }, mb: 0.5 }}>
                      {card.icon}
                    </Typography>
                    <Typography
                      sx={{
                        color: C.brand,
                        fontWeight: 900,
                        fontSize: { xs: '1.2rem', md: '1.5rem' },
                        letterSpacing: '-0.5px',
                      }}
                    >
                      {card.value}
                    </Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', mt: 0.25 }}>
                      {card.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
        {/* Bottom shimmer line */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 1,
            background: `linear-gradient(90deg, transparent, rgba(0,191,165,0.4), transparent)`,
          }}
        />
      </Box>

      {/* ── Feature micro-grid (replaces marquee) ────────────────────────────────── */}
      <Box sx={{ bgcolor: '#fff', borderBottom: `1px solid ${C.border}` }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
              borderLeft: `1px solid ${C.border}`,
            }}
          >
            {[
              { icon: Bolt, text: 'Trucks move within minutes of booking' },
              { icon: Route, text: 'Direct pickup — no hub sorting' },
              { icon: QrCode2, text: 'Digital proof of delivery' },
              { icon: AccountBalanceWallet, text: 'Pay per kg — no full truck needed' },
              { icon: Smartphone, text: 'Live GPS on every shipment' },
              { icon: Shield, text: 'Insured logistics, every consignment' },
              { icon: People, text: 'Vetted drivers, verified vehicles' },
              { icon: DoneAll, text: 'Cities, highways & industrial corridors' },
            ].map(({ icon: Icon, text }, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.4,
                  px: { xs: 2, md: 3 },
                  py: { xs: 2, md: 2.2 },
                  borderRight: `1px solid ${C.border}`,
                  borderBottom: {
                    xs: `1px solid ${C.border}`,
                    sm: i < 4 ? `1px solid ${C.border}` : 'none',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha(C.brand, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ color: C.brand, fontSize: 16 }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '0.72rem', md: '0.78rem' },
                    color: C.textMid,
                    fontWeight: 500,
                    lineHeight: 1.3,
                  }}
                >
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Configurable stats ────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: C.surface, py: { xs: 4.5, md: 5.5 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s) => (
              <Grid item xs={6} md={3} key={s.label}>
                <Stack alignItems="center" spacing={0.5}>
                  <s.icon sx={{ color: C.brand, fontSize: 25, mb: 0.4 }} />
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '1.5rem', md: '1.9rem' },
                      color: C.textDark,
                      lineHeight: 1,
                    }}
                  >
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.77rem', color: C.textMuted, textAlign: 'center' }}>
                    {s.label}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Relay network photo banner ───────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: '#0f172a',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, #0f172a 0%, rgba(15,23,42,0.88) 40%, rgba(15,23,42,0.3) 70%, rgba(15,23,42,0) 100%)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
        <Image
          src="/assets/truck_fleet.png"
          alt="TerraKite relay network — trucks connected in real time"
          width={1440}
          height={480}
          style={{
            width: '100%',
            height: '360px',
            objectFit: 'cover',
            objectPosition: 'center 30%',
            display: 'block',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            px: { xs: 3, md: 10 },
          }}
        >
          <Box sx={{ maxWidth: 520 }}>
            <Chip
              label="RELAY NETWORK"
              size="small"
              sx={{
                bgcolor: alpha(C.brand, 0.18),
                color: C.brand,
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: 1.2,
                border: `1px solid ${alpha(C.brand, 0.3)}`,
                mb: 2,
              }}
            />
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.7rem', md: '2.6rem' },
                lineHeight: 1.2,
                mb: 1.5,
                letterSpacing: -0.5,
              }}
            >
              <HeadlineRotator
                headlines={[
                  'Your consignment moves the moment you book.',
                  'No hub. No waiting. Cargo rolls instantly.',
                  'Book in 60 seconds. Truck dispatched in minutes.',
                  'From your door — straight to the destination.',
                ]}
              />
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.62)',
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.75,
                maxWidth: 440,
              }}
            >
              No hub. No wait. Spare truck capacity connects directly from your door to the
              destination — across cities, highways, and industrial corridors.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Who We Serve — CLEAN ICON GRID ──────────────────────────────── */}
      <Box
        id="sectors"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: '#0f172a',
        }}
      >
        <Container maxWidth="lg">
          <Stack alignItems="center" mb={8}>
            <SectionBadge label={t('landing.sectors.badge')} />
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2rem', md: '2.8rem' },
                color: '#fff',
                textAlign: 'center',
                letterSpacing: -0.7,
                mb: 3,
                maxWidth: 900,
              }}
            >
              {t('landing.sectors.title')}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '1rem',
                textAlign: 'center',
                maxWidth: 640,
                lineHeight: 1.8,
              }}
            >
              {t('landing.sectors.subtitle')}
            </Typography>
          </Stack>

          {/* 4-column clean icon grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
              gap: { xs: 2, md: 2.5 },
            }}
          >
            {[
              { icon: Inventory2, key: 's1', color: '#22c55e' },
              { icon: Business, key: 's2', color: '#06b6d4' },
              { icon: TwoWheeler, key: 's3', color: '#f97316' },
              { icon: LocalShipping, key: 's4', color: '#eab308' },
              { icon: Bolt, key: 's5', color: '#0ea5e9' },
              { icon: Verified, key: 's6', color: '#a855f7' },
              { icon: Inventory2, key: 's7', color: '#ec4899' },
              { icon: Business, key: 's8', color: '#8b5cf6' },
            ].map(({ icon: Icon, key, color }) => (
              <Box
                key={key}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.08)',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  transition: 'all 0.25s ease',
                  textAlign: 'center',
                  '&:hover': {
                    borderColor: alpha(color, 0.35),
                    bgcolor: alpha(color, 0.06),
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    bgcolor: alpha(color, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    border: `1px solid ${alpha(color, 0.2)}`,
                  }}
                >
                  <Icon sx={{ fontSize: 26, color }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    color: '#fff',
                    mb: 0.5,
                  }}
                >
                  {t(`landing.sectors.${key}Title`)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.82rem',
                    color: 'rgba(255,255,255,0.5)',
                    lineHeight: 1.5,
                  }}
                >
                  {t(`landing.sectors.${key}Desc`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Divider />

      {/* ── Shipping tiers — 3-column equal-height grid ───────────────────── */}
      <Box id="pricing" sx={{ py: { xs: 7, md: 10 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Stack alignItems="center" mb={6}>
            <SectionBadge label={t('landing.sectionBadges.shippingPlans')} />
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                color: C.textDark,
                textAlign: 'center',
                mb: 1.5,
                letterSpacing: -0.5,
              }}
            >
              {t('landing.pricing.title')}
            </Typography>
            <Typography
              sx={{ fontSize: '1rem', color: C.textMuted, textAlign: 'center', maxWidth: 520 }}
            >
              {t('landing.pricing.subtitle')}
            </Typography>
          </Stack>

          {/* Shipping Plans Row */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2.5, md: 3 }}
            alignItems="stretch"
            sx={{
              width: '100%',
              pt: 2.5,
            }}
          >
            {shippingTiers.map((tier, i) => (
              <Box key={tier.name} sx={{ flex: 1, minWidth: 0 }}>
                <TierCard tier={tier} featured={i === 1} />
              </Box>
            ))}
          </Stack>

          <Stack alignItems="center" mt={5}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ color: C.textMuted, fontSize: '0.87rem' }}>
                {t('landing.pricing.customPlan')}
              </Typography>
              <Button
                variant="text"
                endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                sx={{ color: C.brand, fontWeight: 700, textTransform: 'none', p: 0, minWidth: 0 }}
                onClick={() => window.open(`mailto:${brandingConfig.contact.supportEmail}`)}
              >
                {t('landing.pricing.contactSales')}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <Box id="how" sx={{ py: { xs: 7, md: 10 }, bgcolor: C.surface }}>
        <Container maxWidth="lg">
          <Stack alignItems="center" mb={7}>
            <SectionBadge label={t('landing.sectionBadges.howItWorks')} color={C.brandDark} />
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                color: C.textDark,
                textAlign: 'center',
                letterSpacing: -0.5,
              }}
            >
              {t('landing.howItWorks.title')}
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 4, md: 2 }}
            alignItems={{ xs: 'center', md: 'flex-start' }}
          >
            {[
              {
                icon: Psychology,
                title: t('landing.howItWorks.step1Title'),
                desc: t('landing.howItWorks.step1Desc'),
              },
              {
                icon: AccountBalanceWallet,
                title: t('landing.howItWorks.step2Title'),
                desc: t('landing.howItWorks.step2Desc'),
              },
              {
                icon: TwoWheeler,
                title: t('landing.howItWorks.step3Title'),
                desc: t('landing.howItWorks.step3Desc'),
              },
              {
                icon: DoneAll,
                title: t('landing.howItWorks.step4Title'),
                desc: t('landing.howItWorks.step4Desc'),
              },
            ].map((step, i, arr) => (
              <HowStep
                key={step.title}
                number={i + 1}
                icon={step.icon}
                title={step.title}
                desc={step.desc}
                isLast={i === arr.length - 1}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── Comparison Table — TerraKite vs Traditional vs FTL ─────────── */}
      <Box id="compare" sx={{ py: { xs: 6, md: 9 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Stack alignItems="center" mb={5}>
            <SectionBadge label={t('landing.comparison.badge')} />
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.7rem', md: '2.4rem' },
                color: C.textDark,
                textAlign: 'center',
                letterSpacing: -0.5,
                mb: 1,
              }}
            >
              {t('landing.comparison.title')}
            </Typography>
            <Typography
              sx={{ color: C.textMuted, fontSize: '0.95rem', textAlign: 'center', maxWidth: 540 }}
            >
              {t('landing.comparison.subtitle')}
            </Typography>
          </Stack>

          <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                minWidth: 620,
                borderCollapse: 'separate',
                borderSpacing: 0,
                '& th, & td': {
                  px: { xs: 1.5, md: 2.5 },
                  py: 1.8,
                  fontSize: { xs: '0.78rem', md: '0.87rem' },
                  textAlign: 'left',
                  borderBottom: `1px solid ${C.border}`,
                },
                '& th': { fontWeight: 800, color: C.textDark },
                '& td': { color: C.textMid },
                '& thead th': {
                  bgcolor: C.surface,
                  borderBottom: `2px solid ${C.border}`,
                  fontSize: { xs: '0.72rem', md: '0.78rem' },
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  color: C.textMuted,
                },
                '& thead th:nth-of-type(2)': {
                  bgcolor: alpha(C.brand, 0.08),
                  color: C.brand,
                  fontWeight: 900,
                  borderBottom: `2px solid ${C.brand}`,
                },
                '& tbody td:nth-of-type(2)': {
                  color: C.brandDark,
                  fontWeight: 700,
                  bgcolor: alpha(C.brand, 0.04),
                },
                '& tbody tr:last-child td': { borderBottom: 'none' },
              }}
            >
              <thead>
                <tr>
                  <th>{t('landing.comparison.feature')}</th>
                  <th>{t('landing.comparison.terrakite')}</th>
                  <th>{t('landing.comparison.traditional')}</th>
                  <th>{t('landing.comparison.fullTruck')}</th>
                </tr>
              </thead>
              <tbody>
                {['r1', 'r2', 'r3', 'r4', 'r5', 'r6', 'r7', 'r8'].map((r) => (
                  <tr key={r}>
                    <th>{t(`landing.comparison.${r}`)}</th>
                    <td>{t(`landing.comparison.${r}tk`)}</td>
                    <td>{t(`landing.comparison.${r}tc`)}</td>
                    <td>{t(`landing.comparison.${r}ft`)}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── When to Use TerraKite — COMPACT ICON ROWS (dark) ──────────── */}
      <Box
        id="when-to-use"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: '#0f172a',
        }}
      >
        <Container maxWidth="lg">
          <Stack alignItems="center" mb={8}>
            <SectionBadge label={t('landing.whenToUse.badge')} />
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2rem', md: '2.8rem' },
                color: '#fff',
                textAlign: 'center',
                letterSpacing: -0.7,
                mb: 3,
                maxWidth: 900,
              }}
            >
              {t('landing.whenToUse.title')}
            </Typography>
          </Stack>

          {/* Compact 2-column layout with left icon + right text */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            {[
              { icon: LocalShipping, key: 'use1', color: '#06b6d4' },
              { icon: AccessTime, key: 'use2', color: '#14b8a6' },
              { icon: TwoWheeler, key: 'use3', color: '#f97316' },
              { icon: Bolt, key: 'use4', color: '#fbbf24' },
              { icon: Route, key: 'use5', color: '#a78bfa' },
              { icon: CurrencyRupee, key: 'use6', color: '#22c55e' },
            ].map(({ icon: Icon, key, color }) => (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2.5,
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.08)',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: alpha(color, 0.35),
                    bgcolor: alpha(color, 0.06),
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2.5,
                    bgcolor: alpha(color, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: `1px solid ${alpha(color, 0.2)}`,
                  }}
                >
                  <Icon sx={{ color, fontSize: 26 }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#fff',
                      mb: 0.3,
                    }}
                  >
                    {t(`landing.whenToUse.${key}Title`)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.88rem',
                      color: 'rgba(255,255,255,0.55)',
                      lineHeight: 1.5,
                    }}
                  >
                    {t(`landing.whenToUse.${key}Desc`)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Sectors we serve — REMOVED (moved above pricing) ──────────────── */}

      {/* ── Platform features — 2-col layout: features left, dashboard right ──── */}
      <Box id="features" sx={{ py: { xs: 7, md: 10 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Stack alignItems="center" mb={6}>
            <SectionBadge label={t('landing.sectionBadges.platformFeatures')} color={C.accent} />
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                color: C.textDark,
                textAlign: 'center',
                letterSpacing: -0.5,
              }}
            >
              {t('landing.platformFeatures.title')}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'flex',
              gap: { xs: 4, md: 5 },
              alignItems: 'flex-start',
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            {/* ── LEFT: Feature cards (2-col grid) ── */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Grid
                container
                spacing={2}
                alignItems="stretch"
                sx={{ '& .MuiGrid-item': { display: 'flex' } }}
              >
                {features.map((f) => (
                  <Grid item xs={6} key={f.title}>
                    <Box sx={{ width: '100%' }}>
                      <FeatureCard {...f} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* ── RIGHT: Platform dashboard preview (mobile-sized) ── */}
            <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
              <Box
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  bgcolor: '#07100f',
                  border: `1px solid ${alpha(C.brand, 0.2)}`,
                  boxShadow: `0 24px 64px rgba(0,0,0,0.22), 0 0 0 1px ${alpha(C.brand, 0.06)}`,
                }}
              >
                {/* Topbar */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.4,
                    bgcolor: '#0d1f1c',
                    borderBottom: `1px solid ${alpha(C.brand, 0.13)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: C.brand,
                        boxShadow: `0 0 6px ${C.brand}`,
                      }}
                    />
                    <Typography
                      sx={{
                        color: C.brand,
                        fontWeight: 800,
                        fontSize: '0.76rem',
                        letterSpacing: 0.9,
                      }}
                    >
                      TERRAKITE · Live Dashboard
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        px: 1.2,
                        py: 0.3,
                        borderRadius: 1,
                        bgcolor: alpha(C.brand, 0.13),
                        border: `1px solid ${alpha(C.brand, 0.22)}`,
                      }}
                    >
                      <Typography sx={{ color: C.brand, fontSize: '0.62rem', fontWeight: 700 }}>
                        LIVE
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        bgcolor: alpha(C.brand, 0.18),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ color: C.brand, fontSize: '0.6rem', fontWeight: 800 }}>
                        RK
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Stats then shipments stacked — narrow mobile card */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {/* Left: stat tiles */}
                  <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(C.brand, 0.1)}` }}>
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.33)',
                        fontSize: '0.58rem',
                        fontWeight: 700,
                        letterSpacing: 1.1,
                        textTransform: 'uppercase',
                        mb: 1.4,
                      }}
                    >
                      Overview
                    </Typography>
                    <Stack spacing={1}>
                      {[
                        {
                          label: 'Wallet Balance',
                          value: '₹12,450',
                          sub: '+₹800 today',
                          color: C.brand,
                        },
                        {
                          label: 'Active Shipments',
                          value: '24',
                          sub: 'in transit',
                          color: C.accent,
                        },
                        { label: 'Completed', value: '891', sub: 'this month', color: '#4ade80' },
                      ].map((s) => (
                        <Box
                          key={s.label}
                          sx={{
                            bgcolor: alpha(s.color, 0.07),
                            border: `1px solid ${alpha(s.color, 0.16)}`,
                            borderRadius: 2,
                            p: 1.4,
                          }}
                        >
                          <Typography
                            sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.6rem', mb: 0.3 }}
                          >
                            {s.label}
                          </Typography>
                          <Stack direction="row" alignItems="baseline" spacing={1}>
                            <Typography
                              sx={{
                                color: s.color,
                                fontSize: '1.3rem',
                                fontWeight: 900,
                                lineHeight: 1,
                              }}
                            >
                              {s.value}
                            </Typography>
                            <Typography
                              sx={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.55rem' }}
                            >
                              {s.sub}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                    <Box
                      sx={{
                        mt: 2,
                        py: 0.95,
                        borderRadius: 2,
                        background: `linear-gradient(90deg, ${C.brand}, ${C.accent})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={handleGetStarted}
                    >
                      <Typography sx={{ color: '#000', fontSize: '0.72rem', fontWeight: 800 }}>
                        {isAuthenticated ? 'Open Dashboard' : 'Try for Free →'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right: recent shipments */}
                  <Box sx={{ p: 2 }}>
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.33)',
                        fontSize: '0.58rem',
                        fontWeight: 700,
                        letterSpacing: 1.1,
                        textTransform: 'uppercase',
                        mb: 1.4,
                      }}
                    >
                      Recent Shipments
                    </Typography>
                    <Stack spacing={0.8}>
                      {[
                        {
                          id: 'TK-9821',
                          from: 'Vizag Port',
                          to: 'Hyderabad',
                          status: 'Delivered',
                          color: '#4ade80',
                          weight: '420 kg',
                        },
                        {
                          id: 'TK-9820',
                          from: 'MVP Colony',
                          to: 'Vijayawada',
                          status: 'In Transit',
                          color: C.accent,
                          weight: '780 kg',
                        },
                        {
                          id: 'TK-9819',
                          from: 'Dwaraka Nagar',
                          to: 'Guntur',
                          status: 'Picked Up',
                          color: C.brand,
                          weight: '210 kg',
                        },
                        {
                          id: 'TK-9818',
                          from: 'RK Beach',
                          to: 'Hyderabad',
                          status: 'Delivered',
                          color: '#4ade80',
                          weight: '550 kg',
                        },
                      ].map((p) => (
                        <Box
                          key={p.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto auto',
                            alignItems: 'center',
                            gap: 1.5,
                            bgcolor: 'rgba(255,255,255,0.025)',
                            borderRadius: 2,
                            px: 1.6,
                            py: 1,
                            border: '1px solid rgba(255,255,255,0.045)',
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ color: C.brand, fontSize: '0.64rem', fontWeight: 700 }}
                            >
                              {p.id}
                            </Typography>
                            <Typography
                              noWrap
                              sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6rem' }}
                            >
                              {p.from} → {p.to}
                            </Typography>
                          </Box>
                          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.58rem' }}>
                            {p.weight}
                          </Typography>
                          <Box
                            sx={{
                              px: 1,
                              py: 0.3,
                              borderRadius: 1,
                              bgcolor: alpha(p.color, 0.12),
                              border: `1px solid ${alpha(p.color, 0.24)}`,
                            }}
                          >
                            <Typography
                              sx={{ color: p.color, fontSize: '0.58rem', fontWeight: 700 }}
                            >
                              {p.status}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Partner section ───────────────────────────────────────────────── */}
      <Box
        id="partners"
        sx={{
          py: { xs: 7, md: 10 },
          bgcolor: '#0f172a',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -80,
            right: -80,
            width: 350,
            height: 350,
            borderRadius: '50%',
            background: C.brandGlow,
            filter: 'blur(80px)',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionBadge label={t('landing.sectionBadges.forPartners')} color={C.brand} />
              <Typography
                component="h2"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '1.8rem', md: '2.6rem' },
                  color: '#fff',
                  lineHeight: 1.2,
                  mb: 2,
                  letterSpacing: -0.5,
                }}
              >
                {t('landing.partners.title')}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.65)',
                  fontSize: '1rem',
                  mb: 3.5,
                  lineHeight: 1.75,
                }}
              >
                {t('landing.partnerSubtitle').split('{pilotLink}')[0]}
                <Box component="span" sx={{ color: C.brand, fontWeight: 700 }}>
                  {brandingConfig.messages.pilotPortalUrl}
                </Box>
                {t('landing.partnerSubtitle').split('{pilotLink}')[1]}
              </Typography>
              <Stack spacing={1.3} mb={4}>
                {[
                  t('landing.partners.feature1'),
                  t('landing.partners.feature2'),
                  t('landing.partners.feature3'),
                  t('landing.partners.feature4'),
                ].map((f) => (
                  <Stack key={f} direction="row" alignItems="center" spacing={1.2}>
                    <CheckCircle sx={{ color: C.brand, fontSize: 16 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.88rem' }}>
                      {f}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  onClick={() => window.open(brandingConfig.messages.pilotPortalUrl, '_blank')}
                  variant="contained"
                  size="large"
                  endIcon={<RocketLaunch sx={{ fontSize: 17 }} />}
                  sx={{
                    bgcolor: C.brand,
                    color: '#000',
                    fontWeight: 800,
                    borderRadius: 3,
                    px: 3.5,
                    textTransform: 'none',
                    '&:hover': { bgcolor: C.accent, color: '#000' },
                    boxShadow: `0 8px 24px ${alpha(C.brand, 0.38)}`,
                  }}
                >
                  {t('landing.partners.joinAsRider')}
                </Button>
                <Button
                  onClick={() => window.open(brandingConfig.messages.pilotPortalUrl, '_blank')}
                  variant="outlined"
                  size="large"
                  endIcon={<Business sx={{ fontSize: 17 }} />}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.32)',
                    color: '#fff',
                    fontWeight: 700,
                    borderRadius: 3,
                    px: 3.5,
                    textTransform: 'none',
                    '&:hover': { borderColor: C.brand, color: C.brand },
                  }}
                >
                  {t('landing.partners.onboardAsFirm')}
                </Button>
              </Stack>
            </Grid>

            {/* Partner feature grid — Mobile optimized */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2} justifyContent="center">
                {[
                  {
                    icon: TwoWheeler,
                    title: t('landing.partnerCards.riderApp'),
                    desc: t('landing.partnerCards.riderAppDesc'),
                    color: C.brand,
                  },
                  {
                    icon: Business,
                    title: t('landing.partnerCards.firmDashboard'),
                    desc: t('landing.partnerCards.firmDashboardDesc'),
                    color: C.accent,
                  },
                  {
                    icon: BarChart,
                    title: t('landing.partnerCards.earningsAnalytics'),
                    desc: t('landing.partnerCards.earningsAnalyticsDesc'),
                    color: C.amber,
                  },
                  {
                    icon: Verified,
                    title: t('landing.partnerCards.verifiedBadge'),
                    desc: t('landing.partnerCards.verifiedBadgeDesc'),
                    color: '#4ade80',
                  },
                  {
                    icon: CurrencyRupee,
                    title: t('landing.partnerCards.instantPayouts'),
                    desc: t('landing.partnerCards.instantPayoutsDesc'),
                    color: C.brand,
                  },
                  {
                    icon: SupportAgent,
                    title: t('landing.partnerCards.dedicatedSupport'),
                    desc: t('landing.partnerCards.dedicatedSupportDesc'),
                    color: '#f472b6',
                  },
                ].map((card) => (
                  <Grid item xs={6} sm={6} key={card.title}>
                    <Box
                      sx={{
                        p: { xs: 2.2, md: 2 },
                        borderRadius: 3,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(8px)',
                        height: '100%',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.1)',
                          transform: 'translateY(-3px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          p: 0.8,
                          borderRadius: 1.5,
                          bgcolor: alpha(card.color, 0.18),
                          width: 'fit-content',
                          mb: 1.2,
                        }}
                      >
                        <card.icon sx={{ color: card.color, fontSize: 19 }} />
                      </Box>
                      <Typography
                        sx={{ color: '#fff', fontWeight: 800, fontSize: '0.88rem', mb: 0.5 }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'rgba(255,255,255,0.55)',
                          fontSize: '0.78rem',
                          lineHeight: 1.6,
                        }}
                      >
                        {card.desc}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Platform Benefits for Riders ──────────────────────────────────── */}
      <Box
        sx={{
          py: { xs: 7, md: 10 },
          background: `linear-gradient(135deg, ${C.brandLight} 0%, #fff 50%, ${C.brandLight} 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Stack alignItems="center" mb={5}>
            <Chip
              label={t('landing.platformBenefits.badge', 'Why Go Digital?')}
              size="small"
              sx={{
                bgcolor: alpha(C.brand, 0.12),
                color: C.brandDark,
                fontWeight: 700,
                fontSize: '0.78rem',
                mb: 2,
              }}
            />
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                color: C.textDark,
                textAlign: 'center',
                letterSpacing: -0.5,
                mb: 1,
              }}
            >
              {t('landing.platformBenefits.title', 'Platform Riders Earn More')}
            </Typography>
            <Typography
              sx={{
                color: C.textMuted,
                fontSize: { xs: '1rem', md: '1.15rem' },
                textAlign: 'center',
                maxWidth: 680,
              }}
            >
              {t(
                'landing.platformBenefits.subtitle',
                "Offline riders miss out on return orders, instant payouts, verification trust, and insurance. Platform riders don't."
              )}
            </Typography>
          </Stack>

          {/* 3x2 grid using CSS grid for proper horizontal distribution */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {[
              {
                icon: Repeat,
                color: C.brand,
                title: t('landing.platformBenefits.returnOrders', 'Return Orders'),
                desc: t(
                  'landing.platformBenefits.returnOrdersDesc',
                  'Platform orders auto-match you for return shipments in the same corridor — double your earnings on a single trip.'
                ),
              },
              {
                icon: Payments,
                color: C.accent,
                title: t('landing.platformBenefits.instantPayouts', 'Instant Digital Payouts'),
                desc: t(
                  'landing.platformBenefits.instantPayoutsDesc',
                  'Every completed delivery is credited to your wallet instantly. No waiting for cash collection — withdraw anytime.'
                ),
              },
              {
                icon: Verified,
                color: '#4ade80',
                title: t('landing.platformBenefits.verifiedTrust', 'Verified & Trusted'),
                desc: t(
                  'landing.platformBenefits.verifiedTrustDesc',
                  'Your verification badge builds sender trust. More senders choose verified riders — that means more orders for you.'
                ),
              },
              {
                icon: Shield,
                color: C.amber,
                title: t('landing.platformBenefits.insurance', 'Parcel Insurance'),
                desc: t(
                  'landing.platformBenefits.insuranceDesc',
                  'Platform deliveries include parcel protection coverage. Deliver with confidence knowing parcels are covered.'
                ),
              },
              {
                icon: BarChart,
                color: '#a78bfa',
                title: t('landing.platformBenefits.earningsTracker', 'Earnings Dashboard'),
                desc: t(
                  'landing.platformBenefits.earningsTrackerDesc',
                  'Track every rupee — daily, weekly, and monthly earnings analytics right in the rider app. Optimize your routes for max earnings.'
                ),
              },
              {
                icon: Speed,
                color: '#f472b6',
                title: t('landing.platformBenefits.priorityOrders', 'Priority Matching'),
                desc: t(
                  'landing.platformBenefits.priorityOrdersDesc',
                  'Active platform riders get first access to high-value orders. The more you deliver, the better orders you receive.'
                ),
              },
            ].map((card) => (
              <Box
                key={card.title}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: '#fff',
                  border: `1px solid ${C.border}`,
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    boxShadow: `0 8px 30px ${alpha(card.color, 0.15)}`,
                    transform: 'translateY(-4px)',
                    borderColor: alpha(card.color, 0.3),
                  },
                }}
              >
                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: 2,
                    bgcolor: alpha(card.color, 0.1),
                    width: 'fit-content',
                    mb: 2,
                  }}
                >
                  <card.icon sx={{ color: card.color, fontSize: 28 }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.05rem',
                    color: C.textDark,
                    mb: 0.8,
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  sx={{
                    color: C.textMuted,
                    fontSize: '0.9rem',
                    lineHeight: 1.7,
                  }}
                >
                  {card.desc}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* CTA for riders */}
          <Stack alignItems="center" mt={5}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => window.open(brandingConfig.links?.riderApp || '/login', '_blank')}
              sx={{
                bgcolor: C.brand,
                color: '#fff',
                fontWeight: 800,
                px: 5,
                py: 1.5,
                borderRadius: 999,
                fontSize: '1rem',
                boxShadow: `0 4px 20px ${alpha(C.brand, 0.4)}`,
                '&:hover': { bgcolor: C.brandDark },
              }}
            >
              {t('landing.platformBenefits.cta', 'Start Earning Today')}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── Testimonials carousel ─────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 7, md: 10 }, bgcolor: C.surface }}>
        <Container maxWidth="lg">
          <Stack alignItems="center" mb={5}>
            <SectionBadge label={t('landing.sectionBadges.customerStories')} color={C.amber} />
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                color: C.textDark,
                textAlign: 'center',
                letterSpacing: -0.5,
              }}
            >
              {t('landing.testimonials.title')}
            </Typography>
          </Stack>
          <TestimonialsCarousel testimonials={testimonials} />
        </Container>
      </Box>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 7, md: 10 }, bgcolor: '#fff' }}>
        <Container maxWidth="md">
          <Stack alignItems="center" mb={5}>
            <SectionBadge label={t('landing.sectionBadges.faq')} color={C.textMuted} />
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                color: C.textDark,
                textAlign: 'center',
                letterSpacing: -0.5,
              }}
            >
              {t('landing.faq.title')}
            </Typography>
          </Stack>
          <Stack spacing={1.5}>
            {faqs.map((faq, i) => (
              <Accordion
                key={i}
                elevation={0}
                sx={{
                  border: `1px solid ${C.border}`,
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { boxShadow: '0 4px 18px rgba(0,0,0,0.06)' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: C.brand }} />}
                  sx={{ px: 3, py: 0.5 }}
                >
                  <Typography sx={{ fontWeight: 700, color: C.textDark, fontSize: '0.93rem' }}>
                    {faq.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}>
                  <Typography sx={{ color: C.textMuted, fontSize: '0.87rem', lineHeight: 1.72 }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── Bottom CTA ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          py: { xs: 7, md: 9 },
          background: `linear-gradient(135deg, #000 0%, #0a1a18 100%)`,
          position: 'relative',
          overflow: 'hidden',
          borderTop: `1px solid ${alpha(C.brand, 0.18)}`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-30%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 600,
            height: 280,
            borderRadius: '50%',
            background: C.brandGlow,
            filter: 'blur(80px)',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.9rem', md: '2.8rem' },
              color: '#fff',
              mb: 1.5,
              letterSpacing: -0.5,
            }}
          >
            {t('landing.bottomCta.title')}
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.58)',
              fontSize: '1.02rem',
              mb: 4,
              maxWidth: 460,
              mx: 'auto',
            }}
          >
            {t('landing.bottomCta.subtitle')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              onClick={handleGetStarted}
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: C.brand,
                color: '#000',
                fontWeight: 800,
                borderRadius: 3,
                px: 4.5,
                py: 1.6,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: C.brandDark, color: '#fff' },
                boxShadow: `0 8px 28px ${alpha(C.brand, 0.38)}`,
              }}
            >
              {isAuthenticated
                ? t('landing.bottomCta.goDashboard')
                : t('landing.bottomCta.createAccount')}
            </Button>
            <Button
              onClick={handleGetPricing}
              disabled={quoteLoading}
              variant="outlined"
              size="large"
              startIcon={
                quoteLoading ? (
                  <CircularProgress size={15} sx={{ color: 'rgba(255,255,255,0.55)' }} />
                ) : null
              }
              sx={{
                borderColor: 'rgba(255,255,255,0.28)',
                color: '#fff',
                fontWeight: 700,
                borderRadius: 3,
                px: 4,
                py: 1.6,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { borderColor: C.brand, color: C.brand },
              }}
            >
              {quoteLoading ? t('common.loading') : t('landing.bottomCta.getPriceQuote')}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── Footer — simplified ───────────────────────────────────────────── */}
      <Box
        component="footer"
        sx={{
          bgcolor: '#000',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          py: { xs: 3.5, md: 4.5 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center" justifyContent="space-between" spacing={3}>
            <Grid item xs={12} sm="auto">
              <Stack spacing={1}>
                <Box>
                  <Image
                    src="/assets/terrakite-teal-logo.png"
                    alt={brandingConfig.brandName}
                    width={120}
                    height={28}
                    style={{
                      objectFit: 'contain',
                      height: '26px',
                      width: 'auto',
                      filter: 'brightness(0) invert(1)',
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.28)' }}>
                  © {new Date().getFullYear()} {brandingConfig.companyLegalName}
                </Typography>
                <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.28)' }}>
                  <a
                    href={`tel:${brandingConfig.contact.supportPhoneRaw}`}
                    style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'none' }}
                  >
                    {brandingConfig.contact.supportPhone}
                  </a>
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm="auto">
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.5, sm: 3.5 }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Typography
                  onClick={() => router.push('/support')}
                  sx={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: '0.86rem',
                    cursor: 'pointer',
                    '&:hover': { color: C.brand },
                    transition: 'color 0.2s',
                  }}
                >
                  {t('landing.footer.support')}
                </Typography>
                <Typography
                  onClick={() => router.push('/estimate')}
                  sx={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: '0.86rem',
                    cursor: 'pointer',
                    '&:hover': { color: C.brand },
                    transition: 'color 0.2s',
                  }}
                >
                  Get an Estimate
                </Typography>
                <Typography
                  onClick={() => router.push('/pilot')}
                  sx={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: '0.86rem',
                    cursor: 'pointer',
                    '&:hover': { color: C.brand },
                    transition: 'color 0.2s',
                  }}
                >
                  Partner Portal
                </Typography>
                <Typography
                  onClick={() => router.push('/terms')}
                  sx={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: '0.86rem',
                    cursor: 'pointer',
                    '&:hover': { color: C.brand },
                    transition: 'color 0.2s',
                  }}
                >
                  {t('landing.footer.terms')}
                </Typography>
                <Typography
                  onClick={() => router.push('/privacy')}
                  sx={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: '0.86rem',
                    cursor: 'pointer',
                    '&:hover': { color: C.brand },
                    transition: 'color 0.2s',
                  }}
                >
                  {t('landing.footer.privacy')}
                </Typography>
                <Box
                  onClick={() => window.open(brandingConfig.messages.pilotPortalUrl, '_blank')}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    cursor: 'pointer',
                    bgcolor: alpha(C.brand, 0.1),
                    border: `1px solid ${alpha(C.brand, 0.22)}`,
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.45,
                    '&:hover': { bgcolor: alpha(C.brand, 0.18) },
                    transition: 'background 0.2s',
                  }}
                >
                  <RocketLaunch sx={{ color: C.brand, fontSize: 13 }} />
                  <Typography sx={{ color: C.brand, fontSize: '0.84rem', fontWeight: 700 }}>
                    {t('landing.footer.joinAsPartner')}
                  </Typography>
                  <OpenInNew sx={{ color: C.brand, fontSize: 11 }} />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
